import { Alert, Pressable, Text, View } from 'react-native';
import { CartaoApoio } from '../components/Recolhivel';
import type { Reg } from '../store/types';
import type { Colecao, Ctx, ModDef, PainelProps } from './tipos';
import { dataBr, din, diasAte, junta, num, op, pct, temNum } from './util';
import { mudarCampos, mudarReg, novoReg, regsDe, salvarVarios } from '../store/modulos';
import { getDoc, setDoc } from '../store/store';
import { newTask } from '../store/planner';
import { calcularParcelas, primeiraParcela, type Sistema } from '../lib/financiamento';
import { C, alpha } from '../components/theme';
import { compartilharTexto } from '../lib/anexos';
import { whatsapp } from '../lib/mapa';
import { semBloqueio } from '../auth/AuthContext';
import { today, ymd } from '../lib/date';
import { N_, t } from '../lib/i18n';

const CAT_ICONE: Record<string, string> = { 'Ferramenta': '🔧', 'Livro': '📕', 'Dinheiro': '💵', 'Eletrônico': '📱', 'Roupa': '👕', 'Jogo': '🎮' };
const emprestei = (r: Reg) => r.c.sentido !== 'Peguei emprestado';
const atrasado = (r: Reg) => !r.c.devolvido && !!r.c.prazo && diasAte(r.c.prazo) < 0;

/** Mensagem educada para lembrar a devolução. */
function lembrete(r: Reg, moeda: string) {
  const coisa = r.c.categoria === 'Dinheiro' && temNum(r.c.valor) ? din(r.c.valor, moeda) : r.c.item;
  return emprestei(r)
    ? t('Oi, {pessoa}! Tudo bem? Passando para lembrar do(a) {coisa} que te emprestei em {data}. Consegue devolver {quando}?', {
      pessoa: r.c.pessoa || '', coisa, data: dataBr(r.c.data), quando: r.c.prazo ? t('até {d}', { d: dataBr(r.c.prazo) }) : t('nos próximos dias') })
    : t('Oi, {pessoa}! Ainda estou com o(a) {coisa} que você me emprestou. Quando fica bom para eu devolver?', { pessoa: r.c.pessoa || '', coisa });
}

const item: Colecao = {
  id: 'item', check: 'devolvido', nome: N_('Objetos e valores'), um: N_('Empréstimo'), icone: r => (r.c.devolvido ? '✅' : CAT_ICONE[r.c.categoria] ?? '📦'),
  campos: [
    { k: 'sentido', r: N_('Situação'), t: 'opcao', op: [N_('Emprestei'), N_('Peguei emprestado')] },
    { k: 'item', r: N_('O quê'), t: 'texto', ph: N_('Ex.: Furadeira, livro, R$ 200') },
    { k: 'categoria', r: N_('Categoria'), t: 'opcao', op: [N_('Objeto'), N_('Ferramenta'), N_('Livro'), N_('Eletrônico'), N_('Roupa'), N_('Jogo'), N_('Dinheiro'), N_('Outro')] },
    { k: 'valor', r: N_('Valor'), t: 'dinheiro' },
    { k: 'pessoa', r: N_('Com quem'), t: 'texto' },
    { k: 'telefone', r: N_('Telefone'), t: 'telefone' },
    { k: 'endereco', r: N_('Endereço de quem está com o item'), t: 'endereco' },
    { k: 'data', r: N_('Data do empréstimo'), t: 'data' },
    { k: 'prazo', r: N_('Devolver até'), t: 'data', aviso: [3, 0] },
    { k: 'devolvido', r: N_('Devolvido'), t: 'sim' },
    { k: 'notas', r: N_('Observações (estado do item...)'), t: 'longo' },
  ],
  titulo: r => r.c.item || '',
  sub: (r, ctx) => junta(emprestei(r) ? t('com {p}', { p: r.c.pessoa || '?' }) : t('de {p}', { p: r.c.pessoa || '?' }), op(r.c.categoria),
    r.c.categoria === 'Dinheiro' && temNum(r.c.valor) && din(r.c.valor, ctx.moeda), r.c.devolvido && r.c.devolvidoEm && t('devolvido em {d}', { d: dataBr(r.c.devolvidoEm) })),
  data: 'prazo', feito: r => !!r.c.devolvido, anexos: true,
  novo: () => ({ sentido: 'Emprestei', data: today(), categoria: 'Objeto' }),
  ajustar: (c, k) => (k === 'devolvido' ? { ...c, devolvidoEm: c.devolvido ? today() : '' } : c),
  filtros: [[N_('Com outras pessoas'), r => emprestei(r) && !r.c.devolvido], [N_('Comigo'), r => !emprestei(r) && !r.c.devolvido],
    [N_('Atrasados'), atrasado], [N_('Devolvidos'), r => !!r.c.devolvido]],
  acoes: (r, ctx) => [
    ...(!r.c.devolvido ? [{ label: '✅ ' + t('Marcar como devolvido'), onPress: () => mudarCampos(ctx.mod, r.id, { devolvido: true, devolvidoEm: today() }) }] : []),
    ...(r.c.telefone ? [{ label: '💬 ' + t('Lembrete pelo WhatsApp'), onPress: () => whatsapp(r.c.telefone, lembrete(r, ctx.moeda)) }] : []),
    { label: '📤 ' + t('Mandar lembrete'), onPress: () => semBloqueio(() => compartilharTexto(lembrete(r, ctx.moeda), r.c.item)).then(x => { if (x === 'copiado') Alert.alert(t('Texto copiado. Cole onde quiser enviar.')); }).catch(() => {}) },
  ],
  agenda: r => (r.c.prazo ? { start: r.c.prazo, title: emprestei(r) ? t('Receber de volta: {i} ({p})', { i: r.c.item, p: r.c.pessoa }) : t('Devolver: {i} ({p})', { i: r.c.item, p: r.c.pessoa }) } : null),
  resumo: l => { const a = l.filter(atrasado).length, fora = l.filter(r => emprestei(r) && !r.c.devolvido).length;
    return fora || a ? junta(t('{n} item(ns) com outras pessoas', { n: fora }), a && '⚠️ ' + t('{n} atrasado(s)', { n: a })) : null; },
  vazio: N_('Anote o que emprestou ou pegou emprestado, com foto e prazo. O app avisa no dia da devolução.'),
};


// ─── Financiamentos e empréstimos com contrato (0.24) ─────────────────────────────────────────────
const tomei = (r: Reg) => r.c.sentido !== 'Concedi';
const parcelasDe = (ctx: Ctx, c: Reg) => ctx.filhos(c.id, 'parcela').sort((a, b) => num(a.c.n) - num(b.c.n));
const atrasada = (p: Reg) => !p.c.pago && !!p.c.venc && diasAte(p.c.venc) < 0;

export function contasContrato(ctx: Ctx, c: Reg) {
  const ps = parcelasDe(ctx, c);
  const pagas = ps.filter(p => p.c.pago);
  const abertas = ps.filter(p => !p.c.pago);
  const saldo = abertas.reduce((s, p) => s + (temNum(p.c.amort) ? num(p.c.amort) : num(p.c.valor)), 0);
  const pago = pagas.reduce((s, p) => s + (temNum(p.c.valorPago) ? num(p.c.valorPago) : num(p.c.valor)), 0);
  const jurosFuturos = abertas.reduce((s, p) => s + num(p.c.juros), 0);
  return { ps, pagas, abertas, saldo, pago, jurosFuturos, proxima: abertas[0] ?? null, atrasadas: abertas.filter(atrasada) };
}

/** Gera as parcelas do contrato. Com "manter", as já pagas ficam como estão e só as em aberto são refeitas. */
function gerarParcelas(ctx: Ctx, c: Reg, manter = false): Reg[] {
  const calc = calcularParcelas(num(c.c.valor), num(c.c.taxa), num(c.c.parcelas), (c.c.sistema || 'Price') as Sistema, c.c.primeira);
  const pagas = new Set(manter ? ctx.filhos(c.id, 'parcela').filter(p => p.c.pago).map(p => num(p.c.n)) : []);
  return calc.filter(x => !pagas.has(x.n)).map(x => novoReg('parcela', c.id, { n: x.n, total: calc.length, venc: x.venc, valor: x.valor, juros: x.juros, amort: x.amort, saldo: x.saldo, pago: false }));
}
function refazer(ctx: Ctx, c: Reg) {
  const novas = gerarParcelas(ctx, c, true);
  const fora = new Set(ctx.filhos(c.id, 'parcela').filter(p => !p.c.pago).map(p => p.id));
  // tarefas do Planner das parcelas apagadas também saem
  const tarefas = ctx.filhos(c.id, 'parcela').filter(p => fora.has(p.id) && p.tarefa).map(p => p.tarefa!);
  if (tarefas.length) setDoc('planner', d => ({ tasks: d.tasks.filter(tk => !tarefas.includes(tk.id)) }));
  setDoc(ctx.mod, d => ({ regs: (d?.regs ?? []).filter(r => !fora.has(r.id)) }));
  salvarVarios(ctx.mod, novas);
  Alert.alert(t('Parcelas recalculadas'), t('{n} parcela(s) em aberto refeitas. As pagas ficaram como estavam.', { n: novas.length }));
}

/** Põe as parcelas em aberto dos próximos 12 meses no My Planner (uma tarefa por parcela, ligada a ela). */
function parcelasNoPlanner(ctx: Ctx, c: Reg) {
  const ate = new Date(); ate.setMonth(ate.getMonth() + 12);
  const lim = ymd(ate);
  const tasks = getDoc('planner').tasks;
  const novas = parcelasDe(ctx, c).filter(p => !p.c.pago && p.c.venc && p.c.venc <= lim && !(p.tarefa && tasks.some(x => x.id === p.tarefa)));
  if (!novas.length) return Alert.alert(t('Tudo certo'), t('As parcelas dos próximos 12 meses já estão no My Planner.'));
  const criadas = novas.map(p => ({ p, tk: newTask({
    title: (tomei(c) ? t('Pagar parcela {n}/{t} · {c}', { n: p.c.n, t: p.c.total, c: tituloContrato(c) }) : t('Receber parcela {n}/{t} · {c}', { n: p.c.n, t: p.c.total, c: tituloContrato(c) })),
    emoji: tomei(c) ? '💸' : '💰', emojiManual: true, area: 'emprestimos', start: p.c.venc, energy: 1,
    notes: din(p.c.valor, ctx.moeda), alerts: { mode: 'none', before: [], itens: [{ tipo: 'hora', hora: '09:00' }] } as any,
    link: { aba: 'emprestimos', id: p.id, label: t('Parcela {n}/{t}', { n: p.c.n, t: p.c.total }) },
  }) }));
  setDoc('planner', d => ({ tasks: [...d.tasks, ...criadas.map(x => x.tk)] }));
  salvarVarios(ctx.mod, criadas.map(x => ({ ...x.p, tarefa: x.tk.id })));
  Alert.alert(t('Pronto'), t('{n} parcela(s) colocada(s) no My Planner.', { n: criadas.length }));
}

const tituloContrato = (r: Reg) => r.c.nome || [op(r.c.tipo), r.c.quem].filter(Boolean).join(' · ');

function PainelContratos({ ctx, lista, abrir }: PainelProps) {
  const hoje = new Date();
  const mes = hoje.toISOString().slice(0, 7);
  const todas = regsDe(ctx.mod).filter(r => r.col === 'parcela');
  const sentidoDe = (p: Reg) => { const c = ctx.porId(p.pai); return c ? tomei(c) : true; };
  const doMes = todas.filter(p => String(p.c.venc).startsWith(mes)).sort((a, b) => String(a.c.venc).localeCompare(b.c.venc));
  const atrasadas = todas.filter(atrasada);
  const pagarMes = doMes.filter(p => sentidoDe(p)).reduce((s, p) => s + num(p.c.valor), 0);
  const receberMes = doMes.filter(p => !sentidoDe(p)).reduce((s, p) => s + num(p.c.valor), 0);
  const falta = doMes.filter(p => !p.c.pago && sentidoDe(p)).reduce((s, p) => s + num(p.c.valor), 0);
  // projeção: próximos 6 meses
  const meses = Array.from({ length: 6 }, (_, i) => { const d = new Date(hoje.getFullYear(), hoje.getMonth() + i, 1); return d.toISOString().slice(0, 7); });
  const proj = meses.map(m => ({ m, v: todas.filter(p => !p.c.pago && sentidoDe(p) && String(p.c.venc).startsWith(m)).reduce((s, p) => s + num(p.c.valor), 0) }));
  const maior = Math.max(1, ...proj.map(x => x.v));
  const nomeMes = (m: string) => { const [a, b] = m.split('-').map(Number); return new Date(a, b - 1, 1).toLocaleDateString(undefined, { month: 'short' }); };
  if (!lista.length) return null;
  return (
    <View style={{ gap: 10 }}>
      <CartaoApoio titulo={'📅 ' + t('Este mês')} chave={'empr:mes'} resumo={pagarMes > 0 ? t('A pagar: {v}', { v: din(pagarMes, ctx.moeda) }) : receberMes > 0 ? t('A receber: {v}', { v: din(receberMes, ctx.moeda) }) : ''} gap={6}>
        {pagarMes > 0 && <Text style={{ color: C.text }}>{t('A pagar: {v}', { v: din(pagarMes, ctx.moeda) })}{falta > 0 && falta < pagarMes ? <Text style={{ color: C.sub }}> · {t('falta {v}', { v: din(falta, ctx.moeda) })}</Text> : null}</Text>}
        {receberMes > 0 && <Text style={{ color: C.text }}>{t('A receber: {v}', { v: din(receberMes, ctx.moeda) })}</Text>}
        {!!atrasadas.length && <Text style={{ color: C.danger, fontWeight: '700' }}>⚠️ {t('{n} parcela(s) em atraso: {v}', { n: atrasadas.length, v: din(atrasadas.reduce((s, p) => s + num(p.c.valor), 0), ctx.moeda) })}</Text>}
        {doMes.map(p => {
          const c = ctx.porId(p.pai);
          return (
            <Pressable key={p.id} onPress={() => abrir(p)} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 3 }}>
              <Pressable hitSlop={8} onPress={() => mudarCampos(ctx.mod, p.id, p.c.pago ? { pago: false, pagoEm: '' } : { pago: true, pagoEm: today(), valorPago: temNum(p.c.valorPago) ? p.c.valorPago : p.c.valor })}>
                <Text style={{ fontSize: 18, color: p.c.pago ? C.ok : C.sub }}>{p.c.pago ? '☑' : '☐'}</Text>
              </Pressable>
              <Text style={{ color: p.c.pago ? C.mute : C.text, flex: 1, textDecorationLine: p.c.pago ? 'line-through' : 'none' }} numberOfLines={1}>
                {dataBr(p.c.venc).slice(0, 5)} · {c ? tituloContrato(c) : ''} ({p.c.n}/{p.c.total})
              </Text>
              <Text style={{ color: c && !tomei(c) ? C.ok : C.text, fontWeight: '700' }}>{din(p.c.valor, ctx.moeda)}</Text>
            </Pressable>
          );
        })}
      </CartaoApoio>
      {proj.some(x => x.v > 0) && (
        <CartaoApoio titulo={'📈 ' + t('Parcelas a pagar nos próximos meses')} chave={'empr:proj'} resumo={din(proj.reduce((s, x) => s + x.v, 0), ctx.moeda)} gap={6}>
          {proj.map(x => (
            <View key={x.m} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Text style={{ color: C.sub, width: 40, fontSize: 12 }}>{nomeMes(x.m)}</Text>
              <View style={{ flex: 1, height: 10, backgroundColor: C.card2, borderRadius: 5, overflow: 'hidden' }}>
                <View style={{ width: `${(x.v / maior) * 100}%`, height: 10, backgroundColor: ctx.cor }} />
              </View>
              <Text style={{ color: C.text, fontSize: 12, width: 92, textAlign: 'right' }}>{din(x.v, ctx.moeda)}</Text>
            </View>
          ))}
        </CartaoApoio>
      )}
    </View>
  );
}

function PainelParcelas({ ctx, pai }: PainelProps) {
  if (!pai) return null;
  const n = contasContrato(ctx, pai);
  if (!n.ps.length) return (
    <Pressable onPress={() => salvarVarios(ctx.mod, gerarParcelas(ctx, pai))} style={{ backgroundColor: ctx.cor, borderRadius: 12, padding: 12, alignItems: 'center' }}>
      <Text style={{ color: '#fff', fontWeight: '800' }}>{t('Gerar as parcelas')}</Text>
    </Pressable>
  );
  const p = pct(n.pagas.length, n.ps.length);
  return (
    <View style={{ backgroundColor: alpha(ctx.cor, 0.12), borderRadius: 14, padding: 12, gap: 6 }}>
      <Text style={{ color: C.text, fontWeight: '800' }}>{t('{p} de {t} parcelas pagas ({pct}%)', { p: n.pagas.length, t: n.ps.length, pct: p })}</Text>
      <View style={{ height: 10, backgroundColor: C.card2, borderRadius: 5, overflow: 'hidden' }}><View style={{ width: `${p}%`, height: 10, backgroundColor: ctx.cor }} /></View>
      <Text style={{ color: C.text }}>{t('Saldo devedor: {v}', { v: din(n.saldo, ctx.moeda) })}</Text>
      <Text style={{ color: C.sub, fontSize: 12 }}>{junta(t('Já pago: {v}', { v: din(n.pago, ctx.moeda) }), n.jurosFuturos > 0 && t('Juros ainda por vir: {v}', { v: din(n.jurosFuturos, ctx.moeda) }))}</Text>
      {!!n.proxima && <Text style={{ color: C.sub, fontSize: 12 }}>{t('Próxima: {d} · {v}', { d: dataBr(n.proxima.c.venc), v: din(n.proxima.c.valor, ctx.moeda) })}</Text>}
      {!!n.atrasadas.length && <Text style={{ color: C.danger, fontWeight: '700' }}>⚠️ {t('{n} parcela(s) em atraso', { n: n.atrasadas.length })}</Text>}
    </View>
  );
}

const contrato: Colecao = {
  id: 'contrato', nome: N_('Financiamentos e empréstimos'), um: N_('Contrato'), anexos: true,
  icone: r => (r.c.status === 'Quitado' ? '✅' : r.c.tipo === 'Financiamento de veículo' ? '🚗' : r.c.tipo === 'Financiamento imobiliário' ? '🏠' : tomei(r) ? '🏦' : '💰'),
  campos: [
    { k: 'sentido', r: N_('Situação'), t: 'opcao', op: [N_('Tomei'), N_('Concedi')] },
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: [N_('Empréstimo pessoal'), N_('Consignado'), N_('Financiamento de veículo'), N_('Financiamento imobiliário'), N_('Cartão parcelado'), N_('Crédito empresarial'), N_('Empréstimo entre pessoas'), N_('Outro')] },
    { k: 'nome', r: N_('Nome (opcional)'), t: 'texto', ph: N_('Ex.: Financiamento do carro') },
    { k: 'quem', r: N_('Banco, financeira ou pessoa'), t: 'texto' },
    { k: 'numero', r: N_('Número do contrato'), t: 'texto' },
    { k: 'telefone', r: N_('Telefone'), t: 'telefone' },
    { k: 'valor', r: N_('Valor emprestado ou financiado'), t: 'dinheiro' },
    { k: 'taxa', r: N_('Juros ao mês'), t: 'numero', un: N_('% ao mês'), mostrar: c => c.sistema !== 'Sem juros' },
    { k: 'sistema', r: N_('Forma das parcelas'), t: 'opcao', op: [N_('Price'), N_('SAC'), N_('Sem juros')] },
    { k: 'parcelas', r: N_('Número de parcelas'), t: 'numero' },
    { k: 'primeira', r: N_('Vencimento da 1ª parcela'), t: 'data' },
    { k: 'valorParcela', r: N_('Valor da parcela (1ª)'), t: 'dinheiro' },
    { k: 'status', r: N_('Status'), t: 'opcao', op: [N_('Vigente'), N_('Quitado'), N_('Renegociado')] },
    { k: 'notas', r: N_('Observações (garantia, seguro, CET...)'), t: 'longo' },
  ],
  titulo: r => tituloContrato(r),
  sub: (r, ctx) => {
    const n = contasContrato(ctx, r);
    return junta(tomei(r) ? t('Tomei') : t('Concedi'), n.ps.length ? t('{p}/{t} pagas', { p: n.pagas.length, t: n.ps.length }) : din(r.c.valor, ctx.moeda),
      n.ps.length && r.c.status !== 'Quitado' && t('saldo {v}', { v: din(n.saldo, ctx.moeda) }), n.atrasadas.length && '⚠️ ' + t('{n} em atraso', { n: n.atrasadas.length }));
  },
  feito: r => r.c.status === 'Quitado',
  ordem: 'criado',
  novo: () => ({ sentido: 'Tomei', sistema: 'Price', status: 'Vigente', tipo: 'Empréstimo pessoal' }),
  ajustar: (c, k) => (['valor', 'taxa', 'parcelas', 'sistema'].includes(k)
    ? { ...c, valorParcela: primeiraParcela(num(c.valor), c.sistema === 'Sem juros' ? 0 : num(c.taxa), num(c.parcelas), (c.sistema || 'Price') as Sistema) || c.valorParcela }
    : c),
  semear: (ctx, r) => gerarParcelas(ctx, r),
  filtros: [[N_('Vigentes'), r => r.c.status !== 'Quitado'], [N_('Em atraso'), (r, ctx) => contasContrato(ctx, r).atrasadas.length > 0],
    [N_('Tomei'), r => tomei(r)], [N_('Concedi'), r => !tomei(r)], [N_('Quitados'), r => r.c.status === 'Quitado']],
  acoes: (r, ctx) => [
    { label: '📅 ' + t('Pôr as parcelas no My Planner'), onPress: () => parcelasNoPlanner(ctx, r) },
    { label: '🔁 ' + t('Recalcular as parcelas em aberto'), onPress: () => Alert.alert(t('Recalcular as parcelas?'), t('As parcelas em aberto são refeitas com o valor, os juros e o prazo atuais do contrato. As pagas não mudam.'), [
      { text: t('Cancelar'), style: 'cancel' }, { text: t('Recalcular'), onPress: () => refazer(ctx, r) }]) },
    ...(r.c.status !== 'Quitado' ? [{ label: '✅ ' + t('Marcar como quitado'), onPress: () => mudarCampos(ctx.mod, r.id, { status: 'Quitado' }) }] : []),
    ...(r.c.telefone ? [{ label: '💬 WhatsApp', onPress: () => whatsapp(r.c.telefone, '') }] : []),
  ],
  resumo: (l, ctx) => {
    const vig = l.filter(r => r.c.status !== 'Quitado');
    const devo = vig.filter(tomei).reduce((s, r) => s + contasContrato(ctx, r).saldo, 0);
    const recebo = vig.filter(r => !tomei(r)).reduce((s, r) => s + contasContrato(ctx, r).saldo, 0);
    return devo || recebo ? junta(devo > 0 && t('Você deve: {v}', { v: din(devo, ctx.moeda) }), recebo > 0 && t('A receber: {v}', { v: din(recebo, ctx.moeda) })) : null;
  },
  painel: PainelContratos,
  vazio: N_('Cadastre o financiamento ou empréstimo com o valor, os juros e o número de parcelas. O app calcula as parcelas, o saldo devedor e avisa os vencimentos.'),
};

const parcela: Colecao = {
  id: 'parcela', pai: 'contrato', nome: N_('Parcelas'), um: N_('Parcela'), check: 'pago', anexos: true, data: 'venc',
  pagamento: { tipo: (r, ctx) => { const c = ctx.porId(r.pai); return c && !tomei(c) ? 'receber' : 'pagar'; }, venc: 'venc', data: 'pagoEm', valorPago: 'valorPago', forma: 'forma', umaMoeda: true },
  icone: r => (r.c.pago ? '✅' : atrasada(r) ? '⚠️' : '🧾'),
  campos: [
    { k: 'n', r: N_('Número da parcela'), t: 'numero' },
    { k: 'venc', r: N_('Vencimento'), t: 'data', aviso: [3, 0, -1] },
    { k: 'valor', r: N_('Valor'), t: 'dinheiro' },
    { k: 'juros', r: N_('Juros'), t: 'dinheiro' },
    { k: 'amort', r: N_('Amortização'), t: 'dinheiro' },
    { k: 'saldo', r: N_('Saldo depois desta parcela'), t: 'dinheiro' },
    { k: 'pago', r: N_('Paga'), t: 'sim' },
    { k: 'pagoEm', r: N_('Data do pagamento'), t: 'data', mostrar: c => !!c.pago },
    { k: 'valorPago', r: N_('Valor pago (com multa ou desconto)'), t: 'dinheiro', mostrar: c => !!c.pago },
    { k: 'forma', r: N_('Forma de pagamento'), t: 'opcao', op: [N_('PIX'), N_('Boleto'), N_('Débito automático'), N_('Transferência'), N_('Cartão'), N_('Dinheiro')], mostrar: c => !!c.pago },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
  ],
  titulo: r => t('Parcela {n}/{t}', { n: r.c.n ?? '?', t: r.c.total ?? '?' }),
  sub: (r, ctx) => junta(din(r.c.valor, ctx.moeda), r.c.pago ? t('paga em {d}', { d: dataBr(r.c.pagoEm) }) : dataBr(r.c.venc), r.anexos?.length && '📎 ' + t('comprovante')),
  ordem: (a, b) => num(a.c.n) - num(b.c.n),
  feito: r => !!r.c.pago,
  ajustar: (c, k) => (k === 'pago' ? { ...c, pagoEm: c.pago ? (c.pagoEm || today()) : '', valorPago: c.pago ? (temNum(c.valorPago) ? c.valorPago : c.valor) : c.valorPago } : c),
  agenda: (r, ctx) => { const c = ctx.porId(r.pai); return r.c.venc ? { start: r.c.venc, title: t('Parcela {n}/{t} · {c}', { n: r.c.n, t: r.c.total, c: c ? tituloContrato(c) : '' }), notes: din(r.c.valor, ctx.moeda) } : null; },
  filtros: [[N_('Em aberto'), r => !r.c.pago], [N_('Pagas'), r => !!r.c.pago], [N_('Em atraso'), atrasada], [N_('Sem comprovante'), r => !!r.c.pago && !r.anexos?.length]],
  painel: PainelParcelas,
  vazio: N_('As parcelas aparecem aqui. Anexe o comprovante (foto, PDF ou print do PIX) em cada uma.'),
};

export const EMPRESTIMOS: ModDef = { id: 'emprestimos', colecoes: [contrato, parcela, item] };

